import type { AdminSidebarComponentProps } from '@website-editor/customComponents/JsonComponent.types';
import { CustomJSONBlobComponent, GuestSiteComponentProps } from './types';

/**
 * Creates a lazy loader function for a component config
 */
const createConfigLoader = (
  importFn: () => Promise<Record<string, CustomJSONBlobComponent<AdminSidebarComponentProps, GuestSiteComponentProps>>>,
  configExportName: string,
): (() => Promise<CustomJSONBlobComponent<AdminSidebarComponentProps, GuestSiteComponentProps>>) => {
  return () => importFn().then(m => m[configExportName]);
};

// Lazy loaders - configs are loaded on demand (their internal view components are also lazy-loaded)
const PlaceToStayComponentConfigPromise = () =>
  import(
    /* webpackChunkName: "content/placeToStayComponentConfig" */
    '@website-editor/customComponents/PlaceToStayComponent/PlaceToStayComponent.config'
  );
const ThingsToDoConfigPromise = () =>
  import(
    /* webpackChunkName: "content/thingsToDoConfig" */
    '@website-editor/customComponents/ThingsToDo/ThingsToDo.config'
  );
const DefaultThingsToDoConfigPromise = () =>
  import(
    /* webpackChunkName: "content/defaultThingsToDoConfig" */
    '@website-editor/customComponents/DefaultThingsToDo/DefaultThingsToDo.config'
  );
const GeneralScheduleConfigPromise = () =>
  import(
    /* webpackChunkName: "content/generalScheduleConfig" */
    '@website-editor/customComponents/GeneralSchedule/GeneralSchedule.config'
  );
const FormDisplayConfigPromise = () =>
  import(
    /* webpackChunkName: "content/formDisplayConfig" */
    '@website-editor/customComponents/FormDisplay/FormDisplay.config'
  );
const VipConfigPromise = () =>
  import(
    /* webpackChunkName: "content/vipConfig" */
    '@website-editor/customComponents/Vip/Vip.config'
  );
const WeatherReportConfigPromise = () =>
  import(
    /* webpackChunkName: "content/weatherReportConfig" */
    '@website-editor/customComponents/WeatherReport/WeatherReport.config'
  );
const FaqConfigPromise = () =>
  import(
    /* webpackChunkName: "content/faqConfig" */
    '@website-editor/customComponents/Faq/Faq.config'
  );

// Config loader map - maps component type to its lazy loader
export const CUSTOM_JSON_COMPONENT_CONFIG_LOADERS: ReadonlyArray<{
  loader: () => Promise<CustomJSONBlobComponent<AdminSidebarComponentProps, GuestSiteComponentProps>>;
  componentType: string;
}> = [
  { loader: createConfigLoader(PlaceToStayComponentConfigPromise, 'PlaceToStayComponentConfig'), componentType: 'PlaceToStayComponent' },
  { loader: createConfigLoader(ThingsToDoConfigPromise, 'ThingsToDoConfig'), componentType: 'ThingsToDo' },
  { loader: createConfigLoader(DefaultThingsToDoConfigPromise, 'DefaultThingsToDoConfig'), componentType: 'DefaultThingsToDo' },
  { loader: createConfigLoader(GeneralScheduleConfigPromise, 'GeneralScheduleConfig'), componentType: 'GeneralSchedule' },
  { loader: createConfigLoader(FormDisplayConfigPromise, 'FormDisplayConfig'), componentType: 'FormDisplay' },
  { loader: createConfigLoader(VipConfigPromise, 'VipConfig'), componentType: 'Vip' },
  { loader: createConfigLoader(WeatherReportConfigPromise, 'WeatherReportConfig'), componentType: 'WeatherReport' },
  { loader: createConfigLoader(FaqConfigPromise, 'FaqConfig'), componentType: 'Faq' },
];

export type CustomJsonComponentConfig = CustomJSONBlobComponent<AdminSidebarComponentProps, GuestSiteComponentProps>;
